import {
  createFeatureFlagRefreshWindow,
  createFeatureFlagResolver,
  devFeatureFlagKey,
  type FeatureFlagSource,
} from '@littlebird/feature-flags';
import { makeAutoObservable, reaction } from 'mobx';
import posthog from 'posthog-js';
import { useCallback, useSyncExternalStore } from 'react';

import { getEnvVar, isDebug, posthogApiKey } from '@/env';
import { inElectron } from '@/utils/capabilities';

import { FEATURES, PAYLOAD_FEATURES } from './feature-flags.constants';
import { type Feature, type PayloadFeature } from './feature-flags.types';

// Full-stack dev puts backend `FEATURE_FLAG_DEV_PREFIX` or `GITHUB_USERNAME` last so desktop settings win
const configuredDevKeyPrefix =
  getEnvVar('VITE_FEATURE_FLAG_DEV_PREFIX') ||
  getEnvVar('VITE_GITHUB_USERNAME') ||
  getEnvVar('VITE_BACKEND_FEATURE_FLAG_DEV_PREFIX');
const devKeyPrefix =
  getEnvVar('VITE_ENV') === 'development' ? configuredDevKeyPrefix : undefined;

let flagResolver = createFeatureFlagResolver({
  source: {
    getFeatureFlag: () => undefined,
    getFeatureFlagPayload: () => undefined,
  },
  devKeyPrefix,
});

/** `false` for both a flag that is off and one with no payload, so callers fold them into one branch */
function getFeatureFlagValue(featureKey: PayloadFeature): unknown {
  if (!flagResolver.isEnabled(featureKey)) return false;

  return flagResolver.getPayload(featureKey) ?? false;
}

// App sections the enabled_lb_features payload turns on, e.g. journals for paying users
function getBaseFeatures() {
  const enabledBaseFeaturesPayload = getFeatureFlagValue(
    PAYLOAD_FEATURES.ENABLED_LB_FEATURES
  ) as { features: Feature[] } | null;

  return enabledBaseFeaturesPayload?.features ?? [];
}

function getDebugOverrides(): Feature[] {
  if (!isDebug) return [];
  return [
    FEATURES.API_REQUEST_LOGGER,
    FEATURES.LOCAL_TERMINAL,
    FEATURES.CODING_AGENTS,
  ];
}

/**
 * Strips imagePrompt from app parsing configuration before sending to ContextKit.
 * The imagePrompt field is only needed by the backend API for AI processing,
 * not by ContextKit which only uses identifier, name, and urlPattern.
 * This reduces payload size significantly to stay under IPC buffer limits (~64KB).
 */
function stripImagePromptsFromConfig(config: unknown): unknown {
  if (!config || typeof config !== 'object') return config;

  const data = config as Record<string, unknown>;
  const apps = data.screenshotParseableApps;

  if (!Array.isArray(apps)) return config;

  return {
    screenshotParseableApps: apps.map((app) => {
      if (!app || typeof app !== 'object') return app;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { imagePrompt, ...others } = app as Record<string, unknown>;
      return others;
    }),
  };
}

function composeFeatureFlagsJsonString(): string {
  const flags = Object.fromEntries(
    Object.values(FEATURES).map((featureKey) => {
      return [featureKey, flagResolver.isEnabled(featureKey)];
    })
  );

  for (const feature of getBaseFeatures()) {
    flags[feature] = true;
  }

  for (const feature of getDebugOverrides()) {
    flags[feature] = true;
  }

  // Get app parsing config and strip imagePrompt (not needed by ContextKit)
  const rawConfig = getFeatureFlagValue(
    PAYLOAD_FEATURES.APP_PARSING_CONFIGURATION
  );
  const strippedConfig = stripImagePromptsFromConfig(rawConfig);

  // Get meeting app bundle IDs payload
  const meetingAppBundleIds = getFeatureFlagValue(
    PAYLOAD_FEATURES.MEETING_APP_BUNDLE_IDS
  );
  const meetingSites = getFeatureFlagValue(PAYLOAD_FEATURES.MEETING_SITES);

  // Get resource snapshot configuration payload
  const resourceSnapshotConfiguration = getFeatureFlagValue(
    PAYLOAD_FEATURES.SCHEDULED_RESOURCE_SNAPSHOT_COLLECTION
  );
  const meetingSelectedInputSilentNotification = getFeatureFlagValue(
    PAYLOAD_FEATURES.MEETING_SELECTED_INPUT_SILENT_NOTIFICATION
  );
  // Get remote debug logging flag
  const remoteDebugLogging = getFeatureFlagValue(
    PAYLOAD_FEATURES.REMOTE_DEBUG_LOGGING
  );

  // Build envelope format for Swift's FeatureFlagsEnvelope decoder
  const envelope = {
    flags,
    payloads: {
      app_parsing_configuration: strippedConfig || undefined,
      meeting_app_bundle_ids: meetingAppBundleIds || undefined,
      meeting_sites: meetingSites || undefined,
      resource_snapshot_configuration:
        resourceSnapshotConfiguration || undefined,
      meeting_selected_input_silent_notification:
        meetingSelectedInputSilentNotification || undefined,
      remote_debug_logging: remoteDebugLogging || undefined,
    },
  };

  return JSON.stringify(envelope);
}

class FeatureFlagsStore {
  refreshCounter = 0;

  constructor() {
    makeAutoObservable(this);
  }

  get flags(): Record<Feature, boolean> {
    void this.refreshCounter;
    const flags = Object.fromEntries(
      Object.values(FEATURES).map((featureKey) => {
        return [featureKey, flagResolver.isEnabled(featureKey)];
      })
    );

    for (const feature of getBaseFeatures()) {
      flags[feature] = true;
    }

    for (const feature of getDebugOverrides()) {
      flags[feature] = true;
    }

    return flags as Record<Feature, boolean>;
  }

  get payloads(): Record<PayloadFeature, unknown> {
    void this.refreshCounter;
    const payloads = Object.fromEntries(
      Object.values(PAYLOAD_FEATURES).map((featureKey) => {
        return [featureKey, getFeatureFlagValue(featureKey)];
      })
    );

    return payloads as Record<PayloadFeature, unknown>;
  }

  incrementRefresh() {
    this.refreshCounter++;
  }
}

const featureFlagsStore = new FeatureFlagsStore();

export function setFlagSource(source: FeatureFlagSource): void {
  flagResolver = createFeatureFlagResolver({ source, devKeyPrefix });
}

export const hasFeature = (feature: Feature): boolean =>
  featureFlagsStore.flags[feature] ?? false;

/** A flag outside `FEATURES`, such as a plugin's `plugin-<id>`; MobX readers rerun when flags change */
export const isFlagKeyEnabled = (key: string): boolean => {
  void featureFlagsStore.refreshCounter;
  return flagResolver.isEnabled(key);
};

const getFeatureFlagRefreshSnapshot = () => featureFlagsStore.refreshCounter;

// Every React flag hook shares this subscription to the store's refresh counter
const subscribeToFeatureFlagRefresh = (onStoreChange: () => void) =>
  reaction(getFeatureFlagRefreshSnapshot, onStoreChange);

// useSyncExternalStore rechecks the counter after subscribing, so a refresh during render is not missed
const useFeatureFlagRefresh = () =>
  useSyncExternalStore(
    subscribeToFeatureFlagRefresh,
    getFeatureFlagRefreshSnapshot,
    getFeatureFlagRefreshSnapshot
  );

// A flag that is off resolves to `false`, which is not a payload
const readPayload = <T>(feature: PayloadFeature): T | null => {
  const payload = featureFlagsStore.payloads[feature];
  return payload === false ? null : ((payload as T) ?? null);
};

export const getFeaturePayload = <T = unknown>(
  feature: PayloadFeature
): T | null => readPayload<T>(feature);

// Keep the returned function stable between refreshes, then replace it so memoized consumers recalculate
export const useFeature = () => {
  const refreshCounter = useFeatureFlagRefresh();
  return useCallback(
    (feature: Feature) => hasFeature(feature),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The counter versions this function's identity
    [refreshCounter]
  );
};

export const useFeaturePayload = <T = unknown>(
  feature: PayloadFeature
): T | null => {
  useFeatureFlagRefresh();
  return readPayload<T>(feature);
};

export const getFeatureVariant = (
  feature: Feature | PayloadFeature
): string => {
  void featureFlagsStore.refreshCounter;
  return flagResolver.getVariant(feature);
};

export const useFeatureFlagsLoaded = (): boolean => {
  return useFeatureFlagRefresh() > 0;
};

async function sendFeatureFlagsToContextKit() {
  if (!inElectron()) return;

  const { electronApis } = await import('@/features/electron-api');
  const featureFlagsString = composeFeatureFlagsJsonString();
  electronApis.featureFlags.updateFeatureFlags(featureFlagsString);
}

const flagRefreshWindow = createFeatureFlagRefreshWindow();

// PostHog fires its flag callbacks synchronously on a local override, which loads nothing from the server
let isApplyingFlagOverrides = false;

// A flag-gated route reads a flag before PostHog has loaded any: the SDK
// bootstraps async and `hasFeature` reads its in-memory state directly, with
// no wait built in. The timeout keeps a gate from hanging forever when
// PostHog never calls back (offline, blocked); no key means no call ever comes
const FLAGS_READY_TIMEOUT_MS = 3000;
let flagsLoaded = false;
let resolveFlagsReady: (() => void) | undefined;
let flagsReadyPromise: Promise<void> | undefined;

/** A flag-gated route awaits this before reading `hasFeature`, instead of a synchronous read that can race PostHog's own load */
export function featureFlagsReady(): Promise<void> {
  if (flagsLoaded || !posthogApiKey) {
    return Promise.resolve();
  }
  flagsReadyPromise ??= new Promise<void>((resolve) => {
    resolveFlagsReady = resolve;
    setTimeout(resolve, FLAGS_READY_TIMEOUT_MS);
  });
  return flagsReadyPromise;
}

export function onFeatureFlagsLoaded(context?: { errorsLoading?: boolean }) {
  flagsLoaded = true;
  if (!context?.errorsLoading && !isApplyingFlagOverrides) {
    flagRefreshWindow.markLoaded();
  }
  featureFlagsStore.incrementRefresh();
  sendFeatureFlagsToContextKit();
  resolveFlagsReady?.();
}

export const refreshFeatureFlags = () => {
  if (!posthogApiKey || !posthog.__loaded) return;
  if (!flagRefreshWindow.claimRefresh()) return;

  posthog.reloadFeatureFlags();
};

export function invalidateFeatureFlags(): void {
  flagRefreshWindow.invalidate();
  flagsLoaded = false;
  flagsReadyPromise = undefined;
  resolveFlagsReady = undefined;
}

type FeatureFlagOverrides = Record<string, string | boolean>;

// PostHog persists its local overrides under this property and layers them over every flag it serves
const FLAG_OVERRIDES_PROPERTY = '$override_feature_flags';

// Each window runs its own PostHog client, so an override is sent to the other open windows.
// Released builds need it too, for the plugin list internal users open beside the chat mode selector
const flagOverridesChannel = new BroadcastChannel(
  'littlebird-feature-flag-overrides'
);

// PostHog fires its flag callbacks on an override, so the store and ContextKit refresh as they do after a server change
function applyFlagOverrides(overrides: FeatureFlagOverrides) {
  if (!posthog.__loaded) return;
  isApplyingFlagOverrides = true;
  try {
    posthog.featureFlags.overrideFeatureFlags({ flags: overrides });
  } finally {
    isApplyingFlagOverrides = false;
  }
}

flagOverridesChannel.addEventListener(
  'message',
  (event: MessageEvent<FeatureFlagOverrides>) => applyFlagOverrides(event.data)
);

function saveFlagOverrides(overrides: FeatureFlagOverrides) {
  applyFlagOverrides(overrides);
  flagOverridesChannel.postMessage(overrides);
}

const getFlagOverrides = (): FeatureFlagOverrides =>
  posthog.get_property(FLAG_OVERRIDES_PROPERTY) ?? {};

// PostHog keeps the server's values under this property, apart from local overrides
const SERVER_FLAGS_PROPERTY = '$enabled_feature_flags';

const serverFlagResolver = createFeatureFlagResolver({
  source: {
    getFeatureFlag: (key) =>
      (posthog.get_property(SERVER_FLAGS_PROPERTY) ?? {})[key],
    getFeatureFlagPayload: () => undefined,
  },
  devKeyPrefix,
});

// Reads check the prefixed key first, so an override there wins over both server values
const getFlagOverrideKey = (flagKey: string) =>
  devKeyPrefix ? devFeatureFlagKey(flagKey, devKeyPrefix) : flagKey;

/** Takes any flag key, such as a `Feature` or a plugin's `plugin-<id>` */
export const getFeatureFlagOverride = (flagKey: string) =>
  getFlagOverrides()[getFlagOverrideKey(flagKey)];

/** Overrides the flag in every open window, and across reloads, even when PostHog has no such flag; `undefined` removes the override */
export function setFeatureFlagOverride(
  flagKey: string,
  enabled: boolean | undefined
) {
  const overrideKey = getFlagOverrideKey(flagKey);
  const otherOverrides = Object.fromEntries(
    Object.entries(getFlagOverrides()).filter(
      ([overriddenKey]) => overriddenKey !== overrideKey
    )
  );
  saveFlagOverrides(
    enabled === undefined
      ? otherOverrides
      : { ...otherOverrides, [overrideKey]: enabled }
  );
}

/** A switch that matches the server clears the override instead of saving it, so a later change on the server reaches the user */
export const switchFeatureFlag = (flagKey: string, enabled: boolean) =>
  setFeatureFlagOverride(
    flagKey,
    enabled === serverFlagResolver.isEnabled(flagKey) ? undefined : enabled
  );

export const clearFeatureFlagOverrides = () => saveFlagOverrides({});

/** Flags this user overrode on this computer, from the flags panel or the plugin list; MobX readers rerun when it changes */
export const getFeatureFlagOverrideCount = () => {
  void featureFlagsStore.refreshCounter;
  return Object.keys(getFlagOverrides()).length;
};

/** Features that read as enabled whatever their flag says */
export const getForcedFeatures = (): Feature[] => [
  ...getBaseFeatures(),
  ...getDebugOverrides(),
];
